"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Building2, ChevronLeft, ChevronRight, Images, Play, X } from "lucide-react";
import { generateImageURL } from "@/lib/utils";
import { projectVideos } from "@/lib/v2/video";
import { VideoLightbox } from "./VideoLightbox";
import type { ProjectProperty } from "./types";

export function ProjectGallery({ property, mode = "hero" }: { property: ProjectProperty; mode?: "hero" | "grid" }) {
  const files = property.images?.property_images ?? [];
  const images = files.map((file) =>
    generateImageURL(property.name, "property_images", file),
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const bannerUrl = property.banner_video
    ? generateImageURL(property.name, "banner_video", property.banner_video)
    : "";
  const videos = projectVideos(property);
  const [videoOpen, setVideoOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const bannerRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      setReducedMotion(query.matches);
      if (query.matches) bannerRef.current?.pause();
    };
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (activeIndex == null) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowRight") {
        setActiveIndex((current) =>
          current == null ? 0 : (current + 1) % images.length,
        );
      }
      if (event.key === "ArrowLeft") {
        setActiveIndex((current) =>
          current == null ? 0 : (current - 1 + images.length) % images.length,
        );
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [activeIndex, images.length]);

  if (images.length === 0 && !bannerUrl) {
    return (
      <div className="flex min-h-72 items-center justify-center rounded-2xl border border-line bg-surface px-6 text-center md:min-h-96">
        <div>
          <Building2 aria-hidden="true" className="mx-auto size-10 text-ink-faint" />
          <p className="mt-3 font-display text-lg font-semibold text-ink">
            Renders coming soon
          </p>
        </div>
      </div>
    );
  }

  const image = (
    src: string,
    index: number,
    className: string,
    priority = false,
  ) => (
    <button
      key={`${index}-${src}`}
      type="button"
      onClick={() => setActiveIndex(index)}
      className={`relative overflow-hidden bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${className}`}
      aria-label={`Open photo ${index + 1} of ${images.length}`}
    >
      <Image
        src={src}
        alt={`${property.title || property.name} photo ${index + 1}`}
        fill
        unoptimized
        priority={priority}
        className="object-cover transition-transform duration-300 hover:scale-[1.02]"
        sizes={index === 0 ? "(min-width: 1024px) 60vw, 100vw" : "(min-width: 1024px) 20vw, 50vw"}
      />
    </button>
  );

  // First hero tile: the banner clip when there is one, otherwise the first
  // photo with a "Play video" button on top when YouTube videos exist.
  const heroTile = (className: string) => {
    if (bannerUrl) {
      return (
        <div className={`relative overflow-hidden bg-surface ${className}`}>
          <video
            ref={bannerRef}
            autoPlay={!reducedMotion}
            controls={reducedMotion}
            muted
            loop
            playsInline
            preload="metadata"
            poster={images[0] || undefined}
            aria-label={`${property.title || property.name} video`}
            className="size-full object-cover"
          >
            <source src={bannerUrl} type="video/mp4" />
          </video>
        </div>
      );
    }
    if (videos.length === 0) return image(images[0], 0, className, true);
    return (
      <div className={`relative overflow-hidden ${className}`}>
        {image(images[0], 0, "size-full", true)}
        <button
          type="button"
          onClick={() => setVideoOpen(true)}
          className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-white py-3 pl-4 pr-5 text-sm font-semibold text-ink shadow-[0_8px_30px_rgba(23,29,46,0.25)] transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Play aria-hidden="true" className="size-4 fill-current" />
          Play video
        </button>
      </div>
    );
  };

  return (
    <>
      {mode === "grid" ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {images.map((src, index) => image(src, index, "aspect-[4/3] rounded-xl"))}
        </div>
      ) : images.length >= 3 ? (
        <div className="relative grid h-[330px] grid-cols-2 gap-2 overflow-hidden rounded-2xl md:h-[520px] md:grid-cols-[1.55fr_1fr]">
          {heroTile("row-span-2 rounded-l-2xl")}
          <div
            className={`row-span-2 grid h-full min-h-0 gap-2 ${
              images.length === 3
                ? "grid-cols-1 grid-rows-2"
                : "grid-cols-2 grid-rows-2"
            }`}
          >
            {images.slice(1, 5).map((src, offset) =>
              image(
                src,
                offset + 1,
                `h-full min-h-0 ${images.length === 4 && offset === 2 ? "col-span-2" : ""}`,
              ),
            )}
          </div>
          <button
            type="button"
            onClick={() => setActiveIndex(0)}
            className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink shadow-[0_8px_30px_rgba(23,29,46,0.12)] transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Images aria-hidden="true" className="size-4" />
            See all {images.length} photos
          </button>
        </div>
      ) : (
        <div className="grid aspect-[21/9] min-h-64 overflow-hidden rounded-2xl">
          {heroTile("size-full rounded-2xl")}
        </div>
      )}

      <VideoLightbox
        id={videoOpen ? videos[0]?.id ?? null : null}
        onClose={() => setVideoOpen(false)}
        label={`${property.title || property.name} video`}
      />

      {activeIndex != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 p-4 md:p-8"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveIndex(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${property.title || property.name} photo gallery`}
            className="relative h-[82vh] w-full max-w-6xl"
          >
            <Image
              src={images[activeIndex]}
              alt={`${property.title || property.name} photo ${activeIndex + 1}`}
              fill
              unoptimized
              className="object-contain"
              sizes="100vw"
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveIndex((current) => ((current ?? 0) - 1 + images.length) % images.length)}
                  aria-label="Previous photo"
                  className="absolute left-0 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <ChevronLeft aria-hidden="true" className="size-6" />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveIndex((current) => ((current ?? 0) + 1) % images.length)}
                  aria-label="Next photo"
                  className="absolute right-0 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <ChevronRight aria-hidden="true" className="size-6" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setActiveIndex(null)}
              aria-label="Close gallery"
              className="absolute right-0 top-0 rounded-full bg-white p-3 text-ink shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
            <p className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-ink">
              {activeIndex + 1} of {images.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
